import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  HardDrive,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
  Activity,
  Zap,
  TrendingUp,
  Table as TableIcon,
  Database,
  Info,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { CustomDropdown } from '../../../components/CustomDropdown ';

interface PostgresFileIOViewProps {
  activeCluster: string;
  timeRange: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

// const PALETTE = [
//   '#3b82f6', 
//   '#10b981', 
//   '#f59e0b', 
//   '#ef4444', 
//   '#8b5cf6', 
//   '#06b6d4', 
//   '#ec4899', 
//   '#14b8a6', 
//   '#f97316', 
//   '#84cc16', 
//   '#6366f1', 
//   '#e11d48', 
//   '#a855f7', 
//   '#0ea5e9', 
//   '#d97706', 
//   '#059669', 
//   '#7c3aed', 
//   '#db2777', 
//   '#4f46e5', 
//   '#2563eb', 
//   '#64748b', 
// ];

// const ScrollableLegend = (props: any) => {
//   const { payload, hoveredSeries, onHoverSeries } = props;
//   if (!payload || payload.length === 0) return null;
//   return (
//     <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
//       {payload.map((entry: any, index: number) => {
//         const seriesKey = entry.dataKey || entry.value;
//         const isHovered = hoveredSeries === seriesKey;
//         const isDimmed = hoveredSeries !== null && !isHovered;
//         return (
//           <div
//             key={`legend-item-${index}`}
//             onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
//             onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
//             className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
//               }`}
//           >
//             <span
//               className="w-2 h-2 rounded-full inline-block shrink-0"
//               style={{ backgroundColor: entry.color }}
//             />
//             <span
//               className="text-gray-700 dark:text-gray-200 text-[10px] truncate max-w-[180px] font-mono"
//               title={entry.value}
//             >
//               {entry.value}
//             </span>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

// const ChartTooltip = ({ active, payload, label }: any) => {
//   if (active && payload && payload.length) {
//     return (
//       <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[150px] z-50">
//         {label !== undefined && label !== null && label !== '' && (
//           <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
//             {label}
//           </div>
//         )}
//         {payload.map((entry: any, index: number) => (
//           <div key={index} className="flex justify-between items-center gap-4">
//             <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
//               <span
//                 className="inline-block w-2 h-2 rounded-full shrink-0"
//                 style={{ backgroundColor: entry.color || entry.stroke || entry.fill || '#3b82f6' }}
//               />
//               <span className="truncate max-w-[140px] font-mono">{entry.name || entry.dataKey}</span>
//             </span>
//             <span className="font-bold text-gray-900 dark:text-white font-mono whitespace-nowrap">
//               {typeof entry.value === 'number'
//                 ? `${entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} KB/s`
//                 : (entry.value ?? '-')}
//             </span>
//           </div>
//         ))}
//       </div>
//     );
//   }
//   return null;
// };

export const PostgresFileIOView: React.FC<PostgresFileIOViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType = 'postgresql',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fileIOData, setFileIOData] = useState<any>(null);
  const [isApplicable, setIsApplicable] = useState<boolean>(true);
  const [selectedDbFilter, setSelectedDbFilter] = useState<string>('all');
  // const [selectedTableFilter, setSelectedTableFilter] = useState<string>('all');
  const [tableSearchQuery, setTableSearchQuery] = useState<string>('');
  // const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);

  // Pagination for tables
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,

      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/file_io', { params });
      const raw = res.data;
      if (raw && typeof raw === 'object') {
        if (raw.applicable !== undefined) {
          setIsApplicable(Boolean(raw.applicable));
        }
        setFileIOData(raw.data || raw);
      } else {
        setFileIOData(null);
      }
    } catch (err) {
      console.warn('PostgresFileIOView fetch error:', err);
      setFileIOData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // useEffect(() => {
  //   setSelectedTableFilter('all');
  // }, [selectedDbFilter]);

  const labels: string[] = fileIOData?.labels || [];
  const series: Record<string, (number | null)[]> = fileIOData?.series || {};
  const groups: string[] = Object.keys(series);
  const summary = fileIOData?.summary;

  // const formatTimeLabels = (lbls: string[]) => {
  //   if (!lbls || lbls.length === 0) return [];
  //   const minutes = parseInt(timeRange || '60', 10);
  //   const seen: { [key: string]: number } = {};
  //   return lbls.map((lbl) => {
  //     const d = new Date(lbl);
  //     let base = isNaN(d.getTime())
  //       ? lbl
  //       : minutes > 1440
  //         ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  //         : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  //     if (seen[base] !== undefined) {
  //       seen[base] += 1;
  //       base = `${base}${' '.repeat(seen[base])}`;
  //     } else {
  //       seen[base] = 0;
  //     }
  //     return base;
  //   });
  // };

  // const timeLabels = formatTimeLabels(labels);

  // Formatted data points for recharts
  // const formattedData = useMemo(() => {
  //   if (!labels || labels.length === 0) return [];
  //   return labels.map((_, idx) => {
  //     const point: Record<string, any> = { time: timeLabels[idx] };
  //     groups.forEach((g) => {
  //       const val = series[g]?.[idx];
  //       point[g] = val !== undefined && val !== null ? Number(Number(val).toFixed(2)) : 0;
  //     });
  //     return point;
  //   });
  // }, [labels, series, groups, timeLabels]);

  // const hasData = (arr: any[]) => {
  //   if (!arr || arr.length === 0) return false;
  //   return groups.length > 0 && arr.length > 0;
  // };

  // const getOpacity = (hovered: string | null, key: string) => {
  //   if (hovered === null) return 1;
  //   return hovered === key ? 1 : 0.15;
  // };

  // Available database options from summary or tables
  const availableDatabases = useMemo(() => {
    const dbs = new Set<string>();
    if (summary?.database_distribution) {
      summary.database_distribution.forEach((d: any) => {
        if (d.database && d.database.trim()) dbs.add(d.database);
      });
    }
    if (summary?.tables) {
      summary.tables.forEach((t: any) => {
        if (t.database && t.database.trim()) dbs.add(t.database);
      });
    }
    // Also parse from series keys if needed: "sampledb.public.orders" -> "sampledb"
    groups.forEach((g) => {
      const parts = g.split('.');
      if (parts.length >= 3 && parts[0]) {
        dbs.add(parts[0]);
      }
    });
    return Array.from(dbs).sort();
  }, [summary, groups]);

  const dbDropdownOptions = useMemo(() => {
    return [
      { label: 'All Databases', value: 'all' },
      ...availableDatabases.map((db) => ({ label: db, value: db })),
    ];
  }, [availableDatabases]);

  // Filter series/groups for chart based on selected DB
  // const filteredChartGroups = useMemo(() => {
  //   if (selectedDbFilter === 'all') return groups;
  //   return groups.filter((g) => {
  //     if (g.startsWith(`${selectedDbFilter}.`)) return true;
  //     const matchingTable = summary?.tables?.find(
  //       (t: any) =>
  //         `${t.database}.${t.schema}.${t.table}` === g ||
  //         `${t.schema}.${t.table}` === g ||
  //         t.table === g
  //     );
  //     return matchingTable?.database?.toLowerCase() === selectedDbFilter.toLowerCase();
  //   });
  // }, [groups, selectedDbFilter, summary]);

  // const tableDropdownOptions = useMemo(() => {
  //   return [
  //     { label: `All Tables (${filteredChartGroups.length})`, value: 'all' },
  //     ...filteredChartGroups.map((t) => {
  //       const parts = t.split('.');
  //       const shortName = parts.length >= 2 ? parts.slice(-2).join('.') : t;
  //       return { label: shortName, value: t };
  //     }),
  //   ];
  // }, [filteredChartGroups]);

  // const chartVisibleGroups = useMemo(() => {
  //   if (selectedTableFilter === 'all') {
  //     return filteredChartGroups;
  //   }
  //   return [selectedTableFilter];
  // }, [selectedTableFilter, filteredChartGroups]);

  // Tables list for bottom data table
  const allTablesList: any[] = useMemo(() => {
    if (Array.isArray(summary?.tables) && summary.tables.length > 0) {
      return summary.tables;
    }
    // Fallback if summary.tables not provided
    return groups.map((g) => {
      const parts = g.split('.');
      const db = parts.length >= 3 ? parts[0] : 'postgres';
      const schema = parts.length >= 3 ? parts[1] : (parts.length === 2 ? parts[0] : 'public');
      const table = parts.length >= 3 ? parts.slice(2).join('.') : (parts.length === 2 ? parts[1] : g);

      const arr = series[g] || [];
      const nonNulls = arr.filter((v): v is number => v !== null && v !== undefined);
      const curr = nonNulls.length > 0 ? nonNulls[nonNulls.length - 1] : 0;
      const peak = nonNulls.length > 0 ? Math.max(...nonNulls) : 0;
      const sum = nonNulls.reduce((acc, v) => acc + v, 0);
      const avg = nonNulls.length > 0 ? sum / nonNulls.length : 0;

      return {
        database: db,
        schema,
        table,
        current_read_kbps: curr,
        average_read_kbps: avg,
        peak_read_kbps: peak,
        cache_hit_pct: null,
        samples: nonNulls.length,
        total_samples: labels.length,
        status: peak > 1000 ? 'High' : peak > 100 ? 'Elevated' : 'Normal',
      };
    });
  }, [summary, groups, series, labels]);

  // Filtered tables by selected DB & search query
  const filteredTables = useMemo(() => {
    return allTablesList.filter((t) => {
      const matchesDb =
        selectedDbFilter === 'all' ||
        (t.database && t.database.toLowerCase() === selectedDbFilter.toLowerCase());
      const query = tableSearchQuery.toLowerCase().trim();
      const matchesSearch =
        !query ||
        (t.table && t.table.toLowerCase().includes(query)) ||
        (t.schema && t.schema.toLowerCase().includes(query)) ||
        (t.database && t.database.toLowerCase().includes(query));
      return matchesDb && matchesSearch;
    });
  }, [allTablesList, selectedDbFilter, tableSearchQuery]);

  // Paginated tables
  const totalPages = Math.ceil(filteredTables.length / itemsPerPage) || 1;
  const paginatedTables = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredTables.slice(start, start + itemsPerPage);
  }, [filteredTables, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDbFilter, tableSearchQuery, itemsPerPage]);

  const formatKBps = (kbps: number | undefined | null) => {
    if (kbps === undefined || kbps === null || isNaN(kbps)) return '0.00 KB/s';
    if (kbps >= 1024 * 1024) {
      return `${(kbps / (1024 * 1024)).toFixed(2)} GB/s`;
    }
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(2)} MB/s`;
    }
    return `${Number(kbps).toFixed(2)} KB/s`;
  };

  const getStatusBadge = (status: string) => {
    const st = (status || 'Normal').toLowerCase();
    if (st === 'high') {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-500/30">
          High
        </span>
      );
    }
    if (st === 'elevated') {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
          Elevated
        </span>
      );
    }
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
        Normal
      </span>
    );
  };

  // Max peak in database distribution for progress bars
  const maxDbPeak = useMemo(() => {
    if (!summary?.database_distribution || summary.database_distribution.length === 0) return 1;
    const max = Math.max(
      ...summary.database_distribution.map((d: any) => Number(d.peak_read_kbps ?? d.peak_kbps) || 0)
    );
    return max > 0 ? max : 1;
  }, [summary]);

  if (loading) {
    return (
      <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-orange-600 text-white shadow-md">
            <HardDrive className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">File I/O</h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30">
                {(dbType || 'PostgreSQL').toUpperCase()}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Database table read I/O throughput & activity monitoring
            </p>
          </div>
        </div>

        {/* Header Right: Status Badge & Sample Info */}
        <div className="flex items-center gap-3">
          {labels.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Monitoring Active
            </span>
          ) : (
            <span className="text-gray-700 text-xs dark:text-gray-200 font-semibold">No Samples</span>
          )}
        </div>
      </div>

      {/* Non-applicable notice */}
      {!isApplicable && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-xl text-amber-800 dark:text-amber-300 text-xs">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span>File I/O table metrics are not currently applicable or enabled for this PostgreSQL cluster.</span>
        </div>
      )}

      {/* Caveat Notice Banner */}
      {summary?.caveat && (
        <div className="flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 rounded-xl text-amber-800 dark:text-amber-300 text-xs shadow-sm">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div className="leading-relaxed">
            <span className="font-bold">PostgreSQL I/O Note: </span>
            <span>{summary.caveat}</span>
          </div>
        </div>
      )}

      {/* Top 4 KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* CURRENT READ I/O */}
        <div className="bg-gray-50/70 dark:bg-gray-900 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                CURRENT READ I/O
              </span>
              <Activity className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
            </div>
            <div className="text-xl font-bold font-mono text-gray-900 dark:text-white">
              {formatKBps(summary?.current_read_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Latest recorded read throughput</div>
        </div>

        {/* PEAK READ I/O */}
        <div className="bg-orange-50/70 dark:bg-gray-900 border border-orange-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-orange-700 dark:text-gray-400 uppercase tracking-wider">
                PEAK READ I/O
              </span>
              <Zap className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
            </div>
            <div className="text-xl font-bold font-mono text-orange-600 dark:text-orange-400">
              {formatKBps(summary?.peak_read_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Maximum observed read throughput</div>
        </div>

        {/* AVERAGE READ I/O */}
        <div className="bg-blue-50/70 dark:bg-gray-900 border border-blue-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-blue-700 dark:text-gray-400 uppercase tracking-wider">
                AVERAGE READ I/O
              </span>
              <TrendingUp className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
            </div>
            <div className="text-xl font-bold font-mono text-blue-600 dark:text-blue-400">
              {formatKBps(summary?.average_read_kbps ?? 0)}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">Average across recorded samples</div>
        </div>

        {/* ACTIVE TABLES */}
        <div className="bg-emerald-50/70 dark:bg-gray-900 border border-emerald-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-md flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-emerald-700 dark:text-gray-400 uppercase tracking-wider">
                ACTIVE TABLES
              </span>
              <TableIcon className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
            </div>
            <div className="text-xl font-bold font-mono text-emerald-600 dark:text-emerald-400">
              {summary?.active_tables ?? allTablesList.filter((t) => t.peak_read_kbps > 0).length}
            </div>
          </div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
            Tables with recorded read activity
            {summary?.latency_ms !== null && summary?.latency_ms !== undefined && (
              <span className="ml-1 font-mono font-semibold">({summary.latency_ms}ms lat)</span>
            )}
          </div>
        </div>
      </div>

      {/* Main Full-Width Chart: Table Read I/O Throughput */}
      {/* <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-4 h-4 text-blue-500 dark:text-blue-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Table Read I/O Throughput
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Read activity by database table over time (KB/s)
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
         
            {availableDatabases.length > 0 && (
              <CustomDropdown
                data={dbDropdownOptions}
                selectedValue={selectedDbFilter}
                onSelect={(val) => setSelectedDbFilter(val)}
                width="w-40 sm:w-44"
                placeholder="Database"
                buttonHeight={8}
              />
            )}

          
            {filteredChartGroups.length > 0 && (
              <CustomDropdown
                data={tableDropdownOptions}
                selectedValue={selectedTableFilter}
                onSelect={(val) => setSelectedTableFilter(val)}
                width="w-56 sm:w-64"
                placeholder="Select Table"
                buttonHeight={8}
              />
            )}
          </div>
        </div>

        <div className="h-72 w-full flex items-center justify-center">
          {hasData(formattedData) ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.2} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={5}>
                  <Label
                    value="Read I/O (KB/s)"
                    angle={-90}
                    position="insideLeft"
                    offset={10}
                    fill="#9ca3af"
                    fontSize={10}
                  />
                </YAxis>
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredSeries}
                      onHoverSeries={setHoveredSeries}
                    />
                  }
                />
                {chartVisibleGroups.map((key) => {
                  const originalIdx = groups.indexOf(key);
                  const color = PALETTE[(originalIdx >= 0 ? originalIdx : 0) % PALETTE.length];
                  return (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={key}
                      stroke={color}
                      strokeOpacity={getOpacity(hoveredSeries, key)}
                      strokeWidth={hoveredSeries === key ? 2.5 : 1.5}
                      dot={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
              No table read I/O telemetry available
            </span>
          )}
        </div>
      </div> */}
    <div className='flex justify-end'>
      {availableDatabases.length > 0 && (
        <CustomDropdown
          data={dbDropdownOptions}
          selectedValue={selectedDbFilter}
          onSelect={(val) => setSelectedDbFilter(val)}
          width="w-40 sm:w-44"
          placeholder="Database"
          buttonHeight={8}
        />
      )}
      </div>

      {/* Middle Row: Database Read I/O Distribution & Highest Read I/O Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Database Read I/O Distribution */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Database className="w-4 h-4 text-blue-500 dark:text-blue-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Database Read I/O Distribution
                </h4>
              </div>
            </div>

            <div className="space-y-3.5 max-h-64 overflow-y-auto custom-scroll pr-1">
              {summary?.database_distribution && summary.database_distribution.length > 0 ? (
                summary.database_distribution.map((d: any, idx: number) => {
                  const peakVal = Number(d.peak_read_kbps ?? d.peak_kbps) || 0;
                  const pct = Math.min(100, Math.max(2, (peakVal / maxDbPeak) * 100));
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">{d.database}</span>
                        <span className="font-mono text-gray-500 dark:text-gray-400 text-[11px]">
                          {formatKBps(peakVal)}{' '}
                          <span className="text-[10px] text-gray-400 dark:text-gray-500">peak read</span>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div
                          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic py-6 text-center">
                  No database distribution data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Highest Read I/O Tables */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Highest Read I/O Tables
                </h4>
              </div>
            </div>

            <div className="space-y-2.5 max-h-64 overflow-y-auto custom-scroll pr-1">
              {summary?.highest_io_tables && summary.highest_io_tables.length > 0 ? (
                summary.highest_io_tables.map((ht: any, idx: number) => {
                  const tableName = ht.table || ht.file_name;
                  const schemaName = ht.schema ? `${ht.schema}.` : '';
                  const dbName = ht.database || 'postgres';
                  const peakVal = Number(ht.peak_read_kbps ?? ht.peak_kbps) || 0;
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800/60 pb-2 last:border-0 last:pb-0"
                    >
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white font-mono">
                          {schemaName}{tableName}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">{dbName}</div>
                      </div>
                      <div className="text-xs font-bold font-mono text-orange-600 dark:text-orange-400">
                        {formatKBps(peakVal)}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-xs text-gray-400 dark:text-gray-500 italic py-6 text-center">
                  No high read I/O tables telemetry available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Table: Database Tables with Local Pagination */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <TableIcon className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Database Tables
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Table-level read throughput statistics</p>
          </div>

          {/* Search Input for Tables */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search table, schema, db..."
              value={tableSearchQuery}
              onChange={(e) => setTableSearchQuery(e.target.value)}
              className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-orange-500/50 w-60 h-8"
            />
            {tableSearchQuery && (
              <button
                onClick={() => setTableSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Table</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Database</th>
                <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Schema</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Current Read</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Average Read</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Peak Read</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Cache Hit %</th>
                <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Samples</th>
                <th className="py-2.5 px-3.5 text-center bg-gray-50 dark:bg-gray-900">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
              {paginatedTables && paginatedTables.length > 0 ? (
                paginatedTables.map((t, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                    <td className="py-2.5 px-3.5">
                      <div className="font-bold text-gray-900 dark:text-gray-200 font-mono">
                        {t.table}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">
                        {t.schema ? `${t.schema}.${t.table}` : t.table}
                      </div>
                    </td>
                    <td className="py-2.5 px-3.5 text-gray-700 dark:text-gray-300 font-medium">
                      {t.database || '-'}
                    </td>
                    <td className="py-2.5 px-3.5">
                      <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                        {t.schema || 'public'}
                      </span>
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-900 dark:text-gray-200">
                      {formatKBps(t.current_read_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-200">
                      {formatKBps(t.average_read_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-orange-600 dark:text-orange-400 font-bold">
                      {formatKBps(t.peak_read_kbps)}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                      {t.cache_hit_pct !== null && t.cache_hit_pct !== undefined
                        ? `${Number(t.cache_hit_pct).toFixed(1)}%`
                        : '-'}
                    </td>
                    <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                      {t.samples} / {t.total_samples || labels.length}
                    </td>
                    <td className="py-2.5 px-3.5 text-center">{getStatusBadge(t.status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="py-10 text-center text-gray-500 dark:text-gray-400 italic">
                    {tableSearchQuery
                      ? `No tables matching "${tableSearchQuery}".`
                      : 'No database table statistics available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Local Pagination Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-3">
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {filteredTables.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Math.min(currentPage * itemsPerPage, filteredTables.length)}
              </span>{' '}
              of <span className="font-semibold text-gray-900 dark:text-white">{filteredTables.length}</span> tables
            </span>

            <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">Per page:</span>
              <ItemsPerPageDropdown
                value={itemsPerPage}
                onChange={(val: any) => {
                  setItemsPerPage(Number(val));
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="First Page"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Previous Page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 font-medium">
              Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of{' '}
              <span className="text-gray-900 dark:text-white font-bold">{totalPages}</span>
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Next Page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
              title="Last Page"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostgresFileIOView;
